import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  AdminRsvp,
  EventAttendee,
  SmsAudience,
  SmsBudgetPublic,
  SmsSettingsPublic,
} from "@tih/shared";
import { uniqueE164Numbers } from "@tih/shared";
import {
  ApiError,
  adminFetchSmsBudget,
  adminSendEventSms,
} from "../../api/client";

const AUDIENCES: { value: SmsAudience; label: string }[] = [
  { value: "checked_in", label: "Checked in tonight" },
  { value: "attendees", label: "Everyone on tonight’s list" },
  { value: "reservations", label: "Reservations" },
  { value: "all_with_phones", label: "Attendees and reservations" },
];

function phonesForAudience(
  audience: SmsAudience,
  attendees: EventAttendee[],
  rsvps: AdminRsvp[],
): Array<string | null> {
  const fromAttendees =
    audience === "reservations"
      ? []
      : attendees
          .filter((a) => (audience === "checked_in" ? a.checkedInAt : true))
          .map((a) => a.mobile);
  const fromRsvps =
    audience === "checked_in" || audience === "attendees"
      ? []
      : rsvps.map((r) => r.phone);
  return [...fromAttendees, ...fromRsvps];
}

export function AdminSmsCompose(props: {
  eventId: number | null;
  attendees: EventAttendee[];
  rsvps: AdminRsvp[];
  sms: SmsSettingsPublic | null;
  onStatus: (kind: "error" | "success", message: string) => void;
}) {
  const { eventId, attendees, rsvps, sms, onStatus } = props;
  const [audience, setAudience] = useState<SmsAudience>("checked_in");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [budget, setBudget] = useState<SmsBudgetPublic | null>(null);

  useEffect(() => {
    void adminFetchSmsBudget()
      .then((res) => setBudget(res.budget))
      .catch(() => setBudget(null));
  }, []);

  const numbers = useMemo(
    () =>
      uniqueE164Numbers(
        phonesForAudience(audience, attendees, rsvps),
        sms?.defaultCountryCode,
      ),
    [audience, attendees, rsvps, sms?.defaultCountryCode],
  );

  const costPerSms = budget?.costPerSmsUgx ?? 25;
  const estimateUgx = numbers.length * costPerSms;
  const overBudget = budget != null && budget.balanceUgx < estimateUgx;

  async function send() {
    if (eventId == null || !message.trim()) return;
    if (
      !confirm(
        `Send this SMS to ${numbers.length} number${numbers.length === 1 ? "" : "s"} ` +
          `(~UGX ${estimateUgx.toLocaleString()})?`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const result = await adminSendEventSms({
        eventId,
        audience,
        message: message.trim(),
      });
      const failed = result.failed > 0 ? ` ${result.failed} failed.` : "";
      const cost =
        result.costUgx != null ? ` Cost UGX ${result.costUgx.toLocaleString()}.` : "";
      onStatus("success", `SMS sent to ${result.sent} of ${result.recipients}.${failed}${cost}`);
      const refreshed = await adminFetchSmsBudget().catch(() => null);
      if (refreshed) setBudget(refreshed.budget);
    } catch (err) {
      onStatus("error", err instanceof ApiError ? err.message : "Could not send SMS");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">Text guests</h2>
      <p className="admin-card-sub">
        Sends through Africa&apos;s Talking to people in this event who have a mobile number.
        {" "}
        For the address book and broadcasts to everyone, use{" "}
        <Link to="/admin/communication">Communication</Link>.
        {!sms?.configured && (
          <>
            {" "}
            Configure credentials in <Link to="/admin/settings/sms">Settings → SMS</Link>.
          </>
        )}
      </p>

      <div className="admin-blog-form">
        <div className="admin-blog-form-fields">
          <label>
            <span>Who</span>
            <select
              value={audience}
              disabled={busy}
              onChange={(e) => setAudience(e.target.value as SmsAudience)}
            >
              {AUDIENCES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <p className="admin-muted">
            {numbers.length} number{numbers.length === 1 ? "" : "s"} ready to send.
            {numbers.length > 0 && (
              <>
                {" "}
                This send will cost ~UGX {estimateUgx.toLocaleString()} ({numbers.length} ×{" "}
                {costPerSms})
                {budget ? ` · balance UGX ${budget.balanceUgx.toLocaleString()}` : ""}.
              </>
            )}
          </p>
          {overBudget && (
            <p className="form-error">
              SMS budget too low.{" "}
              <Link to="/admin/settings/sms">Top up SMS budget</Link>.
            </p>
          )}
          <label>
            <span>Message ({message.length}/1000)</span>
            <textarea
              rows={5}
              maxLength={1000}
              value={message}
              disabled={busy}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Together In Heaven — doors are open. We look forward to seeing you tonight."
            />
          </label>
        </div>
        <div className="admin-blog-form-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              busy ||
              eventId == null ||
              !sms?.configured ||
              !message.trim() ||
              numbers.length === 0 ||
              overBudget
            }
            onClick={() => void send()}
          >
            {busy ? "Sending…" : "Send SMS"}
          </button>
        </div>
      </div>
    </section>
  );
}
